import { useMutation } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useEffect } from 'react';

import {
  FinishOutpatientQuestionnaireEntryDocument,
  GetOutpatientQuestionnaireEntryDetailDocument,
} from '~/graphql/generated/graphql';
import { usePlanFeature } from '~/hooks/use-plan-feature';
import { followupMessageSettingModalState } from '~/state/partials/followup_message_setting_modal/atoms';
import {
  outpatientQuestionnaireEntryProfileDrawerState,
  outpatientQuestionnaireFinishConfirmModalModalState,
  outpatientQuestionnaireNotifyConfirmModalState,
} from '~/state/reception/atoms';

export const useFinishOutpatientQuestionnaireEntry = () => {
  const { outpatientQuestionnaireEntryId } = useAtomValue(
    outpatientQuestionnaireEntryProfileDrawerState,
  );

  const resetFinishConfirmModalState = useResetAtom(
    outpatientQuestionnaireFinishConfirmModalModalState,
  );
  const resetNotifyConfirmModalState = useResetAtom(outpatientQuestionnaireNotifyConfirmModalState);
  const setFollowupModalState = useSetAtom(followupMessageSettingModalState);
  const { hasEnabledFeature: hasEnabledFollowup } = usePlanFeature('followup');

  const [finishOutpatientQuestionnaireEntry, { data, loading: finishing, error }] = useMutation(
    FinishOutpatientQuestionnaireEntryDocument,
    {
      refetchQueries: [
        {
          query: GetOutpatientQuestionnaireEntryDetailDocument,
          variables: { id: outpatientQuestionnaireEntryId },
        },
      ],
    },
  );

  useEffect(() => {
    if (!data?.finishOutpatientQuestionnaireEntry?.outpatientQuestionnaireEntry) return;

    const guestPatientId =
      data.finishOutpatientQuestionnaireEntry.outpatientQuestionnaireEntry.directVisitor?.id ||
      data.finishOutpatientQuestionnaireEntry.outpatientQuestionnaireEntry.lineVisitor?.id ||
      null;
    resetFinishConfirmModalState();
    resetNotifyConfirmModalState();
    if (!hasEnabledFollowup) return;
    setFollowupModalState((_state) => ({
      ..._state,
      isOpen: true,
      guestPatientId: guestPatientId,
    }));
  }, [
    data,
    hasEnabledFollowup,
    resetFinishConfirmModalState,
    resetNotifyConfirmModalState,
    setFollowupModalState,
  ]);

  return { finishOutpatientQuestionnaireEntry, finishing, error };
};
